import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2022 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { createSeparator, LOADING, NOT_FOUND } from "abreact/tree/AbTreeListItem";
import { AbTreeEvent, AbTreeUtils } from "abreact/tree/AbTreeUtils";
import { AbTreeChildNodeImpl } from "abreact/tree/node/AbTreeChildNodeImpl";
import { AbEnriched } from "abreact/utils/enrichment/AbEnriched";
import { AB_ITEM_SEPARATOR } from "abweb/collection/AbItemSeparator";
import { AbKeyMap } from "abweb/collection/AbKeyMap";
import { AbSubscribeStore } from "abweb/collection/AbSubscribeStore";
import { AbAsyncUtils } from "abweb/utils/async/AbAsyncUtils";
import { LogUtils } from "abweb/utils/log/LogUtils";
import { TypeUtils } from "abweb/utils/TypeUtils";
const LOGGER = LogUtils.getLogger(__filename);

/**
 * State store for a batch action. The promise is stored so it can be awaited.
 * visuallyLoading indicates whether a rendered tree should show a busy indicator
 * while the action is happening.
 */

/**
 * An array of tree data paired with utility functions, 
 * expansion state, and caching geared toward being rendered
 * by an AbTree/AbTristateTree/AbFilteredTree etc.
 */
export class AbTreeRootNodeImpl {
  constructor(_data, keyFunction) {
    _defineProperty(this, "_size", undefined);
    // undefined means not yet calculated.
    _defineProperty(this, "_children", LOADING);
    _defineProperty(this, "_subscribeStore", new AbSubscribeStore());
    _defineProperty(this, "_filterSets", undefined);
    _defineProperty(this, "_filteredChildren", undefined);
    _defineProperty(this, "_batchActionState", undefined);
    _defineProperty(this, "depth", 0);
    _defineProperty(this, "subscribe", this._subscribeStore.subscribe);
    _defineProperty(this, "onExpand", (path, doneExpanding) => {
      this._subscribeStore.invoke(AbTreeEvent.EXPAND, path, doneExpanding);
    });
    _defineProperty(this, "onCollapse", path => {
      this._subscribeStore.invoke(AbTreeEvent.COLLAPSE, path);
    });
    _defineProperty(this, "refresh", data => {
      if (!data) {
        if (this._children !== LOADING) {
          this._children.forEach(child => child !== AB_ITEM_SEPARATOR && child.refresh(undefined));
        }
        return;
      }
      this._subscribeStore.invoke(AbTreeEvent.UPDATE);
      return AbAsyncUtils.mapItem(data, this.setChildren)?.();
    });
    _defineProperty(this, "setChildren", roots => {
      const treeDataKeyFunction = root => TypeUtils.itemToKey(AbEnriched.getItem(root.value), this.keyFunction);
      const prevRoots = AbKeyMap.from((this._children === LOADING ? [] : this._children).filter(child => child !== AB_ITEM_SEPARATOR).map(root => [treeDataKeyFunction(root), root]));
      const preprocessedRoots = AbTreeUtils.preprocessTreeArrayWithSeparators(roots);
      this._children = preprocessedRoots.map(root => {
        if (root === AB_ITEM_SEPARATOR) return root;
        const key = treeDataKeyFunction(root);
        const oldRoot = prevRoots.get(key);
        if (oldRoot !== AbKeyMap.NOT_FOUND) {
          oldRoot.refresh(root);
          return oldRoot;
        }
        return new AbTreeChildNodeImpl(root, this, this);
      });
      this._filteredChildren = undefined;
      this.invalidateSize();
    });
    _defineProperty(this, "itemAt", index => {
      if (this._children === LOADING && index === 0 || this._batchActionState?.visuallyLoading) {
        return AbEnriched.create({
          value: LOADING,
          depth: 0,
          index: index
        }, {
          displayOnly: true
        });
      }
      let remainingSize = index;
      for (const child of this.children) {
        if (remainingSize < (child === AB_ITEM_SEPARATOR ? 1 : child.size)) {
          if (child === AB_ITEM_SEPARATOR) return createSeparator({
            index: index,
            depth: 1
          });
          const childItem = child.itemAt(remainingSize);
          if (childItem === AB_ITEM_SEPARATOR) return createSeparator({
            index: index,
            depth: child.depth + 1
          });
          if ("path" in childItem && childItem.path) {
            return AbEnriched.createMaybe({
              depth: childItem.path.length,
              index: index,
              value: childItem.path[childItem.path.length - 1],
              path: childItem.path,
              enrichedPath: childItem.enrichedPath,
              expander: childItem.expander
            }, {
              ...childItem.enrichment,
              alwaysAllowFocus: !!childItem.expander,
              ariaExpanded: childItem.expander?.isExpanded(),
              ariaLevel: childItem.path.length
            });
          } else {
            return AbEnriched.create({
              ...childItem,
              index: index
            }, {
              displayOnly: true
            });
          }
        }
        remainingSize -= child === AB_ITEM_SEPARATOR ? 1 : child.size;
      }
      LOGGER.error(`Invalid tree index r2 ${index}`);
      return AbEnriched.create({
        value: NOT_FOUND,
        index: index,
        depth: 0
      }, {
        displayOnly: true
      });
    });
    _defineProperty(this, "indexOf", item => {
      if (item === AB_ITEM_SEPARATOR || this._batchActionState !== undefined) return -1;
      const path = AbEnriched.getItem(item).path;
      if (path === undefined) return -1;
      // If this lookup fails, it isn't in the tree.
      return AbTreeUtils.indexOf(this, path) ?? -1;
    });
    /**
     * Deletes cached size so it must be re-calculated.
     */
    _defineProperty(this, "invalidateSize", () => {
      if (this._size === undefined) return;
      this._size = undefined;
      if (!this._batchActionState) {
        this._subscribeStore.invoke(AbTreeEvent.UPDATE);
      }
    });
    _defineProperty(this, "doBatchAction", (actionFunc, visuallyLoading) => {
      if (this._batchActionState) {
        if (!this._batchActionState.promise) {
          throw new Error("Don't call doBatchAction from itself synchronously.");
        } else {
          return this._batchActionState.promise.then(() => this.doBatchAction(actionFunc, visuallyLoading), e => LOGGER.error(`Batch action failed: ${e}`));
        }
      }
      this._batchActionState = {
        promise: undefined,
        visuallyLoading: visuallyLoading
      };
      const maybePromise = actionFunc();
      if (!maybePromise) {
        this._batchActionState = undefined;
        this._subscribeStore.invoke(AbTreeEvent.UPDATE);
        return undefined;
      } else {
        this._batchActionState = {
          promise: maybePromise,
          visuallyLoading: visuallyLoading
        };
        return maybePromise.finally(() => {
          this._batchActionState = undefined;
          this._subscribeStore.invoke(AbTreeEvent.UPDATE);
        });
      }
    });
    _defineProperty(this, "path", []);
    _defineProperty(this, "enrichedPath", []);
    _defineProperty(this, "setFilterSets", sets => {
      this._filterSets = sets;
      this._filteredChildren = undefined;
      this.cachedChildren.forEach(child => child !== AB_ITEM_SEPARATOR && child.invalidateFilter());
      this.invalidateSize();
    });
    _defineProperty(this, "find", path => AbTreeUtils.find(this, path));
    _defineProperty(this, "expandPath", path => AbTreeUtils.expandPath(this, path));
    _defineProperty(this, "expandPaths", paths => AbTreeUtils.expandPaths(this, paths));
    _defineProperty(this, "expandAll", () => AbTreeUtils.expandAll(this));
    _defineProperty(this, "collapseAll", () => AbTreeUtils.collapseAll(this));
    _defineProperty(this, "collapsePath", path => AbTreeUtils.collapsePath(this, path));
    _defineProperty(this, "collapsePaths", paths => AbTreeUtils.collapsePaths(this, paths));
    _defineProperty(this, "toggle", path => AbTreeUtils.toggle(this, path));
    _defineProperty(this, "getExpandedPaths", () => AbTreeUtils.getExpandedPaths(this));
    this.keyFunction = keyFunction;
    this.refresh(_data);
  }
  get batchActionBusy() {
    return this._batchActionState !== undefined;
  }
  get size() {
    if (this._batchActionState?.visuallyLoading) return 1;
    if (this._size === undefined) {
      this._size = this.children.reduce((acc, child) => acc + (child === AB_ITEM_SEPARATOR ? 1 : child.size), 0);
    }
    return this._size;
  }
  get children() {
    if (this._children === LOADING) return [];
    if (this._filterSets) {
      const filterSets = this._filterSets;
      if (!this._filteredChildren) {
        this._filteredChildren = this._children.filter(child => child !== AB_ITEM_SEPARATOR && (filterSets.expandedNodes.has(child) || filterSets.matchedNodes.has(child)));
      }
      return this._filteredChildren;
    }
    return this._children;
  }
  get cachedChildren() {
    if (this._children === LOADING) return [];
    return this._children;
  }
  get hasMatchAncestor() {
    return false;
  }
}
//# sourceMappingURL=AbTreeRootNodeImpl.js.map
